<template>
  <div class="dvf">
    <div class="dvf__head">
      <span>소자 구조 <small>{{ title }}</small></span>
      <small class="dvf__hint">층에 마우스를 올리면 물질이, 클릭하면 조건 목록이 보입니다</small>
    </div>

    <svg class="dvf__svg" :viewBox="`0 0 ${VW} ${vh}`" @mouseleave="hover = null">
      <!-- 단면 -->
      <g v-for="s in shapes" :key="s.key" class="dvf__layer" :class="layerClass(s)" @mouseenter="hover = s.key" @click="togglePin(s.key)">
        <polygon v-for="(pts, i) in s.polys" :key="i" :points="pts" :style="{ fill: s.color }" />
      </g>
      <!-- 단(tier) 표시: 왼쪽 세로 괄호 -->
      <g v-for="t in tierMarks" :key="'t' + t.label" class="dvf__tier">
        <line :x1="FX - 6" :y1="t.y0" :x2="FX - 6" :y2="t.y1" />
        <text :x="FX - 9" :y="(t.y0 + t.y1) / 2" :transform="`rotate(-90 ${FX - 9} ${(t.y0 + t.y1) / 2})`">{{ t.label }}</text>
      </g>
      <!-- 범례(위 층이 위에) -->
      <g v-for="(r, i) in legend" :key="'l' + r.key" class="dvf__legend" :class="layerClass(r)"
         :transform="`translate(${LX} ${8 + i * LH})`" @mouseenter="hover = r.key" @click="togglePin(r.key)">
        <rect x="0" y="1" width="10" height="10" rx="2" :style="{ fill: r.color }" />
        <text x="15" y="10">{{ r.label }}<tspan v-if="r.filterCount" class="dvf__badge"> ● {{ r.filterCount }}</tspan></text>
      </g>
    </svg>

    <!-- 툴팁(호버) 또는 고정 패널(클릭) -->
    <div v-if="current" class="dvf__panel" :class="{ 'is-pinned': pinned }">
      <div class="dvf__panel-head">
        <b>{{ current.label }}</b>
        <small>{{ current.sources.length ? current.sources.map((s) => `${s.nodeName} / ${s.name}`).join(' · ') : '이 계열에는 해당 속성 값이 없습니다' }}</small>
        <button v-if="pinned" type="button" title="닫기" @click.stop="pinned = null">×</button>
      </div>
      <div v-if="current.values.length" class="dvf__chips">
        <button v-for="v in current.values" :key="v.pid + '|' + v.value" type="button" class="dvf__chip" :class="{ 'is-on': v.on }"
                :title="`${v.nodeName} / ${v.prop}`" @click.stop="$emit('toggle-value', v.pid, v.value)">
          {{ v.value }} <small>{{ v.count }}</small>
        </button>
        <small v-if="current.more" class="dvf__more">… 외 {{ current.more }}개 — 트리에서 보기</small>
      </div>
      <div v-if="current.sources.length" class="dvf__panel-foot">
        <button type="button" class="dv-btn" @click.stop="$emit('open-layer', current.key, current.sources.map((s) => s.id))">트리에서 펼치기</button>
        <button v-if="current.filterCount" type="button" class="dv-btn" @click.stop="$emit('clear-layer', current.sources.map((s) => s.id))">이 층 조건 해제</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 완성 소자 단면 그림 — "살아 있는 필터".
 * props.layers: 쌓는 순서(아래→위). [{ key, label, core, draw, tier?, sources: [enriched prop], values: [{ pid, value, count, on, nodeName, prop }], more, filterCount }]
 *   (DvSearch.deviceLayers 가 dvLayers.m3d.js 매핑 + 실데이터로 만든다)
 * 그리기: 층을 순서대로 "증착"하듯 표면 높이 배열을 갱신하고, 층마다 (이전 표면 ~ 새 표면) 사이를 다각형으로 채운다.
 *   substrate/band = 전면 평평 / gate = 가운데 사다리꼴 / conformal = 아래 형상을 따라 전면 덮음(경사면 생김)
 *   active = 게이트 위 섬 / sd = 좌우 전극이 경사면을 타고 반도체 끝을 덮음 / planar = 전면 덮고 평탄화 / pad = 가운데 넓은 사다리꼴(MIM 상부 전극)
 * 이벤트: toggle-value(pid, value) / open-layer(key, propIds) / clear-layer(propIds) / hover(key|null)
 */
const COLORS = {
  substrate: "#cfd4da", band: "#e6dcc8", gate: "#f2b632", conformal: "#8fd14f", active: "#e5473c", sd: "#f7c948", planar: "#dbe4ee",
};
const COLOR_BY_KEY = { passivation: "#bcd9f5", ild: "#dbe4ee", n_buffer: "#e6dcc8", bottom_electrode: "#f2b632", top_electrode: "#f7c948", dielectric: "#8fd14f" };
const FW = 190;           // 단면 폭
const FX = 22;            // 단면 왼쪽 여백(단 표시용)
const VW = 320;           // viewBox 폭
const LX = FX + FW + 10;  // 범례 x
const LH = 13;            // 범례 행 높이

export default {
  name: "DvDeviceFigure",
  props: {
    title: { type: String, default: "" },
    layers: { type: Array, required: true },
  },
  data() { return { hover: null, pinned: null, FX, VW, LX, LH }; },
  computed: {
    /** 증착 시뮬레이션 → 층별 다각형(높이 단위, y 는 아래가 0) */
    stack() {
      const N = FW;
      let surf = new Array(N + 1).fill(0);
      const out = [];
      const gL = 62, gR = 128; // 게이트 가로 범위
      const ramp = (x, x0, x1, h, s) => { // 사다리꼴: 양 끝 s 만큼 경사
        if (x < x0 || x > x1) return 0;
        if (x < x0 + s) return (h * (x - x0)) / s;
        if (x > x1 - s) return (h * (x1 - x)) / s;
        return h;
      };
      const addOnTop = (x0, x1, h, s) => { const n = surf.slice(); for (let x = 0; x <= N; x++) n[x] = surf[x] + ramp(x, x0, x1, h, s); return n; };
      const conformal = (x0, x1, t) => {
        const n = surf.slice();
        for (let x = Math.max(0, x0); x <= Math.min(N, x1); x++) {
          let m = 0;
          for (let k = Math.max(x0, x - t); k <= Math.min(x1, x + t); k++) m = Math.max(m, surf[k]);
          n[x] = m + t;
        }
        return n;
      };
      const flatten = (t) => { const top = Math.max(...surf) + t; return new Array(N + 1).fill(top); };
      const polyBetween = (lo, hi, x0 = 0, x1 = N) => {
        const top = [], bot = [];
        for (let x = x0; x <= x1; x++) { if (hi[x] > lo[x] + 0.01) { top.push([x, hi[x]]); bot.push([x, lo[x]]); } }
        return top.length ? [top.concat(bot.reverse())] : [];
      };
      const sdOverlap = 14; // 전극이 반도체 끝을 덮는 폭
      this.layers.forEach((l) => {
        let next, polys;
        switch (l.draw) {
          case "substrate": next = addOnTop(0, N, 18, 0); polys = polyBetween(surf, next); break;
          case "band": next = addOnTop(0, N, 7, 0); polys = polyBetween(surf, next); break;
          case "gate": next = addOnTop(gL, gR, 12, 8); polys = polyBetween(surf, next, gL, gR); break;
          case "conformal": next = conformal(0, N, 8); polys = polyBetween(surf, next); break;
          case "active": next = addOnTop(gL + 2, gR - 2, 9, 2); polys = polyBetween(surf, next, gL + 2, gR - 2); break;
          case "sd": {
            const a = conformal(10, gL + 2 + sdOverlap, 9);
            const b = conformal(gR - 2 - sdOverlap, N - 10, 9);
            next = surf.map((v, x) => Math.max(a[x], b[x]));
            polys = [...polyBetween(surf, a, 10, gL + 2 + sdOverlap), ...polyBetween(surf, b, gR - 2 - sdOverlap, N - 10)];
            break;
          }
          case "planar": next = flatten(6); polys = polyBetween(surf, next); break;
          case "pad": next = addOnTop(gL - 10, gR + 10, 11, 6); polys = polyBetween(surf, next, gL - 10, gR + 10); break;
          default: next = addOnTop(0, N, 6, 0); polys = polyBetween(surf, next);
        }
        const y0 = Math.min(...surf), y1 = Math.max(...next);
        out.push({ ...l, polysH: polys, hLo: y0, hHi: y1 });
        surf = next;
      });
      return { items: out, height: Math.max(...surf) };
    },
    figH() { return this.stack.height; },
    // 범례(legend)는 shapes 에, shapes 는 vh 에 의존하므로 여기서는 layers 길이만 쓴다(순환 참조 방지)
    vh() { return Math.max(this.figH + 16, this.layers.length * LH + 16); },
    /** 높이 좌표 → SVG 좌표(위가 0), 왼쪽 여백 적용 */
    shapes() {
      const base = this.vh - 8;
      return this.stack.items.map((l) => ({
        ...l,
        color: COLOR_BY_KEY[l.key] || COLORS[l.draw] || "#ccc",
        polys: l.polysH.map((pts) => pts.map(([x, h]) => `${(FX + x).toFixed(1)},${(base - h).toFixed(1)}`).join(" ")),
        y0: base - l.hHi, y1: base - l.hLo,
      }));
    },
    legend() {
      return [...this.shapes].reverse().map((l) => ({ ...l }));
    },
    tierMarks() {
      const base = this.vh - 8; const tiers = {};
      this.stack.items.forEach((l) => { if (!l.tier || l.tier === "층간") return; const t = tiers[l.tier] || (tiers[l.tier] = { label: l.tier.split(" ")[0], lo: Infinity, hi: -Infinity }); t.lo = Math.min(t.lo, l.hLo); t.hi = Math.max(t.hi, l.hHi); });
      return Object.values(tiers).map((t) => ({ label: t.label, y0: base - t.hi + 2, y1: base - t.lo - 2 }));
    },
    current() {
      const key = this.pinned || this.hover;
      return key ? this.layers.find((l) => l.key === key) : null;
    },
  },
  watch: {
    hover(v) { this.$emit("hover", v); },
    // 계열(층 구성)이 바뀔 때만 고정 해제 — 값 칩 클릭으로 layers 가 다시 계산될 때는 패널을 유지
    layers(nv, ov) { if ((nv || []).map((l) => l.key).join() !== (ov || []).map((l) => l.key).join()) this.pinned = null; },
  },
  methods: {
    togglePin(key) { this.pinned = this.pinned === key ? null : key; },
    layerClass(l) {
      return { "is-hover": this.hover === l.key, "is-pinned": this.pinned === l.key, "is-filtered": l.filterCount > 0, "is-empty": !l.sources.length, "is-core": l.core };
    },
  },
};
</script>

<style lang="scss" scoped>
.dvf { border: 1px solid #dde6ec; border-radius: 10px; background: #fff; margin-bottom: 10px; overflow: hidden; }
.dvf__head { display: flex; flex-direction: column; gap: 2px; padding: 7px 10px; background: #f3f6f9; font-weight: 700; color: #003f67; font-size: 13.5px;
  small { font-weight: 400; color: #789; font-size: 11px; }
}
.dvf__hint { font-weight: 400; }
.dvf__svg { display: block; width: 100%; height: auto; }
.dvf__layer { cursor: pointer;
  polygon { stroke: rgba(0, 0, 0, 0.28); stroke-width: 0.6; stroke-linejoin: round; transition: filter 0.12s, opacity 0.12s; }
  &.is-empty polygon { opacity: 0.35; stroke-dasharray: 2 2; }
  &.is-filtered polygon { stroke: #d08a00; stroke-width: 1.6; }
  &.is-hover polygon, &.is-pinned polygon { stroke: #003f67; stroke-width: 1.6; filter: brightness(1.08) saturate(1.2); }
  &.is-pinned polygon { stroke-width: 2.2; }
}
.dvf__tier { line { stroke: #9fb3c4; stroke-width: 1; } text { font-size: 8px; fill: #567; text-anchor: middle; } }
.dvf__legend { cursor: pointer;
  rect { stroke: rgba(0, 0, 0, 0.28); stroke-width: 0.6; }
  text { font-size: 9px; fill: #334; }
  &.is-empty { opacity: 0.45; }
  &.is-hover text, &.is-pinned text { fill: #003f67; font-weight: 700; }
  &.is-core text { fill: #223; }
}
.dvf__badge { fill: #d08a00; font-weight: 700; }
.dvf__panel { border-top: 1px solid #eef2f5; padding: 8px 10px; font-size: 12px; background: #fbfdff;
  &.is-pinned { background: #f3fbfe; }
}
.dvf__panel-head { display: flex; align-items: baseline; gap: 6px; margin-bottom: 6px;
  b { color: #003f67; }
  small { flex: 1 1 auto; color: #789; font-size: 11px; }
  button { border: 0; background: none; color: #789; cursor: pointer; font-size: 14px; }
}
.dvf__chips { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 6px; }
.dvf__chip { padding: 2px 8px; border: 1px solid #dde6ec; border-radius: 999px; background: #fff; font-size: 11.5px; color: #334; cursor: pointer; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  small { color: #789; }
  &:hover { border-color: #0dbde9; }
  &.is-on { background: #003f67; border-color: #003f67; color: #fff; small { color: #cfe6f3; } }
}
.dvf__more { color: #789; font-size: 11px; align-self: center; }
.dvf__panel-foot { display: flex; gap: 6px; }
</style>
